import React, { useState, useRef, useEffect } from 'react';
import api from '../api';
import './Gate.css';
import { ShieldIcon } from '../components/Icons';

const STATUS_CONFIG = {
  VALID: { color: '#059669', bg: '#d1fae5', border: '#34d399', icon: 'OK', title: 'Access granted', subtitle: 'Visitor may proceed.' },
  ALREADY_USED: { color: '#d97706', bg: '#fef3c7', border: '#fcd34d', icon: 'DU', title: 'Already used', subtitle: 'This pass was already used for entry.' },
  EXPIRED: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: 'EX', title: 'Pass expired', subtitle: 'This pass is no longer valid.' },
  REVOKED: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: 'RV', title: 'Pass revoked', subtitle: 'This pass has been revoked by faculty.' },
  NOT_FOUND: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: 'NF', title: 'Invalid code', subtitle: 'No pass found with this code.' },
};

export default function Gate() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef();
  const [scanning, setScanning] = useState(false);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const verify = async (passCode) => {
    const trimmed = (passCode || code).trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setResult(null);
    setScanning(true);
    try {
      const res = await api.post('/passes/verify', { code: trimmed });
      const { reason, pass, entryTime } = res.data;
      const cfg = STATUS_CONFIG[reason] || STATUS_CONFIG.NOT_FOUND;
      const resultObj = { ...cfg, pass, entryTime, reason, code: trimmed, time: new Date() };
      setResult(resultObj);
      setHistory(h => [resultObj, ...h.slice(0, 9)]);
    } catch (err) {
      const cfg = STATUS_CONFIG.NOT_FOUND;
      const resultObj = { ...cfg, code: trimmed, time: new Date() };
      setResult(resultObj);
      setHistory(h => [resultObj, ...h.slice(0, 9)]);
    } finally {
      setLoading(false);
      setScanning(false);
      setCode('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') verify(); };

  const reset = () => { setResult(null); setCode(''); inputRef.current?.focus(); };

  return (
    <div className="gate-page">
      <div className="gate-left">
        <div className="gate-shell">
          <div className="gate-header">
            <div className="gate-logo"><ShieldIcon /> Gate security</div>
            <h1 className="gate-title">Gate security</h1>
            <p className="gate-subtitle">Scan a QR code or enter the printed pass code to verify a visitor.</p>
          </div>

          <div className={`scan-area ${scanning ? 'scanning' : ''}`}>
            {scanning ? (
              <div className="scan-animation">
                <div className="scan-frame">
                  <div className="scan-line" />
                  <div className="scan-corner tl" /><div className="scan-corner tr" />
                  <div className="scan-corner bl" /><div className="scan-corner br" />
                </div>
                <p className="scan-hint">Verifying pass…</p>
              </div>
            ) : result ? (
              <div className="scan-result" style={{ borderColor: result.border }}>
                <div className="result-icon" style={{ color: result.color }}>{result.icon}</div>
                <div className="result-status" style={{ color: result.color }}>{result.title}</div>
                <div className="result-subtitle">{result.subtitle}</div>
                {result.pass && (
                  <div className="result-pass-info">
                    <div className="pass-info-name">{result.pass.guestName}</div>
                    <div className="pass-info-detail">
                      {result.pass.profession}{result.pass.organization ? ` · ${result.pass.organization}` : ''}
                    </div>
                    <div className="pass-info-event">{result.pass.event?.title}</div>
                    <div className="pass-info-event">
                      {result.pass.event?.venue}
                      {result.pass.event?.date ? ` · ${new Date(result.pass.event.date).toLocaleDateString('en-IN')}` : ''}
                    </div>
                    <div className="pass-info-host">
                      Host: {result.pass.issuedBy?.name} ({result.pass.issuedBy?.department})
                      {result.pass.issuedBy?.phone ? ` · ${result.pass.issuedBy.phone}` : ''}
                    </div>
                    {result.entryTime && (
                      <div className="previous-entry-banner">
                        Previous entry: {new Date(result.entryTime).toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                )}
                <button type="button" className="btn-verify-again" onClick={reset}>Verify another</button>
              </div>
            ) : (
              <div className="scan-idle">
                <div className="qr-icon">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden>
                    <rect x="4" y="4" width="28" height="28" rx="4" stroke="var(--navy-deep)" strokeWidth="3" fill="none" />
                    <rect x="12" y="12" width="12" height="12" rx="2" fill="var(--navy)" />
                    <rect x="48" y="4" width="28" height="28" rx="4" stroke="var(--navy-deep)" strokeWidth="3" fill="none" />
                    <rect x="56" y="12" width="12" height="12" rx="2" fill="var(--navy)" />
                    <rect x="4" y="48" width="28" height="28" rx="4" stroke="var(--navy-deep)" strokeWidth="3" fill="none" />
                    <rect x="12" y="56" width="12" height="12" rx="2" fill="var(--navy)" />
                    <rect x="48" y="48" width="8" height="8" rx="1" fill="var(--red)" />
                    <rect x="62" y="48" width="8" height="8" rx="1" fill="var(--red)" />
                    <rect x="48" y="62" width="8" height="8" rx="1" fill="var(--red)" />
                    <rect x="62" y="62" width="8" height="8" rx="1" fill="var(--red)" />
                  </svg>
                </div>
                <p className="scan-hint">Use a QR scanner or enter the code below.</p>
              </div>
            )}
          </div>

          {!result && (
            <div className="code-input-section">
              <div className="code-input-row">
                <input
                  ref={inputRef}
                  className="code-input"
                  placeholder="VP-XXXXXXXX"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  onKeyDown={handleKey}
                  maxLength={15}
                  aria-label="Pass code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                />
                <button type="button" className="verify-btn" onClick={() => verify()} disabled={!code.trim() || loading}>
                  {loading ? <span className="spinner" /> : 'Go'}
                </button>
              </div>
              <p className="code-hint">Press Enter or tap Go.</p>
            </div>
          )}
        </div>
      </div>

      <div className="gate-right">
        <div className="gate-right-header">
          <h2 className="gate-history-title">Recent verifications</h2>
          <p className="gate-history-sub">Latest checks in this session</p>
        </div>

        {history.length === 0 ? (
          <div className="empty-state" style={{ padding: '2.5rem 1.25rem' }}>
            <div className="empty-icon">Quiet gate</div>
            <div className="empty-text">No scans yet.</div>
          </div>
        ) : (
          <div className="history-list">
            {history.map((h, i) => (
              <div key={i} className="history-item fade-in" style={{ borderLeft: `4px solid ${h.border || h.color}` }}>
                <div className="history-item-title-row">
                  <div>
                    <div className="history-item-meta">
                      <span style={{ fontSize: '0.9375rem' }}>{h.icon}</span>
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--text-sm)', color: h.color }}>
                        {h.title}
                      </span>
                    </div>
                    {h.pass && <div className="history-guest-name">{h.pass.guestName}</div>}
                    <code className="history-item-code">{h.code}</code>
                  </div>
                  <div className="history-item-time">{h.time?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                {h.pass && (
                  <div className="history-item-extra">{h.pass.profession} · {h.pass.event?.title}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {history.length > 0 && (
          <div className="gate-stats">
            {[
              { label: 'Granted', value: history.filter(h => h.reason === 'VALID').length, color: '#059669' },
              { label: 'Rejected', value: history.filter(h => ['EXPIRED', 'REVOKED', 'NOT_FOUND'].includes(h.reason)).length, color: '#dc2626' },
              { label: 'Duplicates', value: history.filter(h => h.reason === 'ALREADY_USED').length, color: '#d97706' },
            ].map(s => (
              <div key={s.label} className="gate-stat">
                <div className="gate-stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="gate-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
